import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Card,
  CardContent,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Divider,
  IconButton
} from "@mui/material";
import { Add, Info, Delete, CalendarToday } from "@mui/icons-material";
import axios from "axios";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const InterviewScheduleForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const candidateData = location.state || {};

  const [hiringManagers, setHiringManagers] = useState([]);
  const [managerLoadError, setManagerLoadError] = useState(null);
  
  const [interviewRounds, setInterviewRounds] = useState([
    "Technical 1", "Technical 2", "Technical 3", "Architectural Round", "Manager Round", "HR Round"
  ]);
  
  const [openAddRoundDialog, setOpenAddRoundDialog] = useState(false);
  const [newRound, setNewRound] = useState("");
  const [roundError, setRoundError] = useState("");

  // Three proposed dates
  const [proposedDates, setProposedDates] = useState([
    { date: null, time: "" },
    { date: null, time: "" },
    { date: null, time: "" }
  ]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [interview, setInterview] = useState({
    name: candidateData.name || "",
    email: candidateData.email || "",
    phone: candidateData.phone || "", // NEW
    skills: candidateData.skills || "",
    experience: candidateData.experience || "",
    interviewer: "",
    round: "",
    hiringManagerEmail: candidateData.hiringManagerEmail || "",
    positionApplied: candidateData.positionApplied || "",
    interviewMode: "Online",
    venue: ""
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showVenue, setShowVenue] = useState(false);
  const [customManagerEmail, setCustomManagerEmail] = useState(""); // NEW

  const fetchHiringManagers = async () => {
    setManagerLoadError(null);
    try {
      const token = localStorage.getItem('token'); 
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/roles/employees/byrole/Hiring Manager`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );
      
      if (response.data.success && response.data.data) {
        const managers = response.data.data.map(manager => ({
          email: manager.email || manager.companyemail,
          name: `${manager.firstName || ''} ${manager.lastName || ''}`.trim(),
        }));
        setHiringManagers(managers);
      } else {
        throw new Error("Failed to fetch hiring managers");
      }
    } catch (err) {
      //console.error("Error fetching hiring managers:", err);
      setManagerLoadError("Failed to load hiring managers");
    }
  };

  const fetchInterviewRounds = async () => {
    try {
      const token = localStorage.getItem('token'); 
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      const response = await axios.get(`${API_URL}/api/interview-rounds`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
           
          }
        }
      );
      if (response.data && Array.isArray(response.data)) {
        setInterviewRounds(response.data);
        localStorage.setItem('interviewRounds', JSON.stringify(response.data));
      }
    } catch (error) {
      //console.error("Error fetching interview rounds from API:", error);
      const localRounds = localStorage.getItem('interviewRounds');
      if (localRounds) {
        try {
          const parsedRounds = JSON.parse(localRounds);
          setInterviewRounds(parsedRounds);
        } catch (parseError) {
          //console.error("Error parsing local rounds:", parseError);
        }
      }
    }
  };

  useEffect(() => {
    if (candidateData.name) {
      setInterview((prev) => ({
        ...prev,
        name: candidateData.name,
        email: candidateData.email,
        phone: candidateData.phone || "", // NEW
        skills: candidateData.skills || "",
        experience: candidateData.experience || "",
        positionApplied: candidateData.positionApplied || "",
        hiringManagerEmail: candidateData.hiringManagerEmail || ""
      }));
    }
    
    fetchHiringManagers();
    fetchInterviewRounds();
  }, [candidateData]);

  useEffect(() => {
    setShowVenue(interview.interviewMode === 'Offline');
  }, [interview.interviewMode]);

  const handleDateChange = (index, newDate) => {
    const newProposedDates = [...proposedDates];
    newProposedDates[index].date = newDate;
    setProposedDates(newProposedDates);
  };

  const handleTimeChange = (index, time) => {
    const newProposedDates = [...proposedDates];
    newProposedDates[index].time = time;
    setProposedDates(newProposedDates);
  };

  const handleModeChange = (e) => {
    const mode = e.target.value;
    setInterview({ ...interview, interviewMode: mode });
    if (mode === 'Online') {
      setInterview(prev => ({ ...prev, venue: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all 3 dates and times are filled
    const allDatesFilled = proposedDates.every(d => d.date && d.time);
    
    if (!allDatesFilled) {
      setError("Please fill all 3 proposed interview dates and times");
      return;
    }

    // Validate venue if offline
    if (interview.interviewMode === 'Offline' && !interview.venue.trim()) {
      setError("Please provide the venue/address for offline interview");
      return;
    }
    
    setLoading(true);
    setError(null);
  
    const token = localStorage.getItem('token');
    if (!token) {
      alert("No authentication token found. Please log in.");
      setLoading(false);
      return;
    }
    
    const payloadToken = JSON.parse(atob(token.split('.')[1]));
    const company_id = payloadToken.company_id;
  
    // Format proposed dates (prevent timezone offset from changing the date)
    const formattedProposedDates = proposedDates.map(d => {
      let localDateStr = d.date;
      if (d.date instanceof Date) {
        const offsetMs = d.date.getTimezoneOffset() * 60000;
        const localDate = new Date(d.date.getTime() - offsetMs);
        localDateStr = localDate.toISOString().split('T')[0];
      }
      return {
        date: localDateStr,
        time: d.time
      };
    });

    const payload = {
      ...interview,
      hiringManagerEmail: interview.hiringManagerEmail === 'custom' ? customManagerEmail : interview.hiringManagerEmail,
      proposedDates: formattedProposedDates,
      experience: parseInt(interview.experience) || 0,
    };
  
  
    try {
      const response = await axios.post(
        `${API_URL}/api/interviews/propose-dates`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
  
      if (response.status === 201) {
        setSuccess(true);
        setTimeout(() => {
          // Reset form state so it doesn't get stuck disabled
          setSuccess(false);
          setInterview({
            name: "",
            email: "",
            phone: "", // NEW
            skills: "",
            experience: "",
            positionApplied: "",
            hiringManagerEmail: "",
            interviewMode: "Online",
            venue: "",
            round: "",
            interviewer: ""
          });
          setProposedDates([
            { date: null, time: "" },
            { date: null, time: "" },
            { date: null, time: "" }
          ]);
          
          navigate('/candidates');
        }, 3000);
      }
    } catch (err) {
      //console.error("Error proposing interview dates:", err);
      setError(err.response?.data?.message || "Error proposing interview dates");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddRoundDialog = (e) => {
    e.stopPropagation();
    setOpenAddRoundDialog(true);
    setNewRound("");
    setRoundError("");
  };

  const handleCloseAddRoundDialog = () => {
    setOpenAddRoundDialog(false);
  };

  const handleNewRoundChange = (e) => {
    setNewRound(e.target.value);
    if (e.target.value.trim() === "") {
      setRoundError("Round name cannot be empty");
    } else if (interviewRounds.includes(e.target.value.trim())) {
      setRoundError("This round already exists");
    } else {
      setRoundError("");
    }
  };

  const handleAddRound = async () => {
    if (newRound.trim() === "") {
      setRoundError("Round name cannot be empty");
      return;
    }

    if (interviewRounds.includes(newRound.trim())) {
      setRoundError("This round already exists");
      return;
    }

    const updatedRounds = [...interviewRounds, newRound.trim()];

    try {
      const token = localStorage.getItem('token'); 
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
      const payload = JSON.parse(atob(token.split('.')[1]));
      const company_id = payload.company_id;
      
      await axios.post(`${API_URL}/api/interview-rounds`, { round: newRound.trim(),
        
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        
       });
      setInterviewRounds(updatedRounds);
      localStorage.setItem('interviewRounds', JSON.stringify(updatedRounds));
      handleCloseAddRoundDialog();
    } catch (error) {
      //console.error("Error adding round to API:", error);
      setInterviewRounds(updatedRounds);
      localStorage.setItem('interviewRounds', JSON.stringify(updatedRounds));
      handleCloseAddRoundDialog();
    }
  };

  const isDateDisabled = (date) => {
    return date < today;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div style={{ minHeight: "100vh" }}>
        <Box sx={{ maxWidth: 1500, borderRadius: 2, border: '2px solid', borderColor: 'grey.300', boxShadow: 4, mx: 3 ,p:4 }}>
          <Card>
            <Typography
              variant="h4"
              gutterBottom
             
            
              fontWeight="bold"
            >
              Interview Scheduling 
            </Typography>
            
            {success && (
              <Alert severity="success" sx={{ m: 2 }}>
                ✅ Interview dates proposed successfully! Hiring manager will select one date, and then the candidate will be notified automatically via email.
              </Alert>
            )}

            <CardContent>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>📋 Two-Step Process:</strong>
                  <br/>
                  1. HR proposes 3 interview dates
                  <br/>
                  2. Hiring manager receives email to select one date
                  <br/>
                  3. Once selected, confirmation email will be automatically sent to candidate
                </Typography>
              </Alert>

              <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Candidate Name"
                      value={interview.name || ""}
                      onChange={(e) =>
                        setInterview({ ...interview, name: e.target.value })
                      }
                      fullWidth
                      required
                      margin="normal"
                      InputProps={{ readOnly: !!candidateData?.name }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Candidate Email"
                      type="email"
                      value={interview.email || ""}
                      onChange={(e) =>
                        setInterview({ ...interview, email: e.target.value })
                      }
                      fullWidth
                      required
                      margin="normal"
                      InputProps={{ readOnly: !!candidateData?.email }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      label="Candidate Phone Number"
                      type="tel"
                      value={interview.phone || ""}
                      onChange={(e) =>
                        setInterview({ ...interview, phone: e.target.value })
                      }
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: !!candidateData?.phone }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Skills"
                      value={interview.skills || ""}
                      onChange={(e) =>
                        setInterview({ ...interview, skills: e.target.value })
                      }
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: !!candidateData?.skills }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Experience"
                      value={interview.experience || ""}
                      onChange={(e) =>
                        setInterview({ ...interview, experience: e.target.value })
                      }
                      fullWidth
                      margin="normal"
                      InputProps={{ readOnly: !!candidateData?.experience }}
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Job Title"
                      value={interview.positionApplied}
                      onChange={(e) =>
                        setInterview({ ...interview, positionApplied: e.target.value })
                      }
                      fullWidth
                      required
                      margin="normal"
                      InputProps={{ readOnly: !!candidateData?.positionApplied }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required margin="normal">
                      <InputLabel>Hiring Manager Email</InputLabel>
                      <Select
                        label="Hiring Manager Email"
                        value={interview.hiringManagerEmail}
                        onChange={(e) => setInterview({ ...interview, hiringManagerEmail: e.target.value })}
                        inputProps={{ readOnly: !!candidateData?.hiringManagerEmail }}
                      >
                        {managerLoadError && (
                          <MenuItem disabled>
                            <Typography color="error">{managerLoadError}</Typography>
                          </MenuItem>
                        )}
                        
                        {hiringManagers.length === 0 && !managerLoadError && (
                          <MenuItem disabled>
                            <Typography>No hiring managers found</Typography>
                          </MenuItem>
                        )}
                        
                        {hiringManagers.map((manager) => (
                          <MenuItem key={manager.email} value={manager.email}>
                            {manager.name ? `${manager.name} (${manager.email})` : manager.email}
                          </MenuItem>
                        ))}
                        <MenuItem value="custom" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          + Enter Custom Email...
                        </MenuItem>
                      </Select>
                    </FormControl>
                    
                    {interview.hiringManagerEmail === 'custom' && (
                      <TextField
                        label="Custom Manager Email"
                        type="email"
                        value={customManagerEmail}
                        onChange={(e) => setCustomManagerEmail(e.target.value)}
                        fullWidth
                        required
                        margin="normal"
                        autoFocus
                      />
                    )}
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }}>
                  <Chip label="📅 Propose 3 Interview Dates" color="primary" />
                </Divider>

                {proposedDates.map((dateObj, index) => (
                  <Box key={index} sx={{ 
                    bgcolor: 'grey.50', 
                    p: 2, 
                    mb: 2, 
                    borderRadius: 2,
                    border: '2px solid',
                    borderColor: 'grey.300',
                  }}>
                    <Typography variant="h6" gutterBottom >
                      <CalendarToday fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Date Option {index + 1}
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <div style={{ marginTop: '8px', marginBottom: '6px' }}>
                          <DatePicker
                            label={`Date Option ${index + 1}`}
                            value={dateObj.date}
                            onChange={(newDate) => handleDateChange(index, newDate)}
                            disablePast 
                            shouldDisableDate={isDateDisabled}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                required: true,
                                margin: "normal",
                                inputProps: {
                                  readOnly: true,
                                  onKeyDown: (e) => e.preventDefault()
                                }
                              }
                            }}
                          />
                        </div>
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <div style={{ marginTop: '8px', marginBottom: '6px' }}>
                          <TextField
                            label={`Time Option ${index + 1}`}
                            type="time"
                            value={dateObj.time}
                            onChange={(e) => handleTimeChange(index, e.target.value)}
                            fullWidth
                            required
                            InputLabelProps={{ shrink: true }}
                            margin="normal"
                            inputProps={{ 
                              onKeyDown: (e) => e.preventDefault(),
                              min: "00:00",
                              max: "23:59"
                            }}
                          />
                        </div>
                      </Grid>
                    </Grid>
                  </Box>
                ))}

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required margin="normal">
                      <InputLabel>Round</InputLabel>
                      <Select
                        label="Round"
                        value={interview.round}
                        onChange={(e) =>
                          setInterview({ ...interview, round: e.target.value })
                        }
                        endAdornment={
                          <InputAdornment position="end" sx={{ position: 'absolute', right: 28, pointerEvents: 'none' }}>
                            <Button 
                              onClick={handleOpenAddRoundDialog}
                              sx={{ minWidth: 'auto', pointerEvents: 'auto' }}
                            >
                              <Add />
                            </Button>
                          </InputAdornment>
                        }
                      >
                        {interviewRounds.sort().map((round) => (
                          <MenuItem key={round} value={round}>{round}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Interviewer"
                      value={interview.interviewer}
                      onChange={(e) =>
                        setInterview({ ...interview, interviewer: e.target.value })
                      }
                      fullWidth
                      required
                      margin="normal"
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth required margin="normal">
                      <InputLabel>Interview Mode</InputLabel>
                      <Select
                        label="Interview Mode"
                        value={interview.interviewMode}
                        onChange={handleModeChange}
                      >
                        <MenuItem value="Online">Online (Virtual)</MenuItem>
                        <MenuItem value="Offline">Offline (In-Person)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {showVenue && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Venue/Address"
                        value={interview.venue}
                        onChange={(e) =>
                          setInterview({ ...interview, venue: e.target.value })
                        }
                        fullWidth
                        required
                        margin="normal"
                        helperText="e.g., Office Address, Conference Room"
                      />
                    </Grid>
                  )}
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
                
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3, gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={loading || success}
                  >
                    {loading ? "Sending to Hiring Manager..." : "📧 Send to Hiring Manager"}
                  </Button>
                  
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/candidate-filter')}
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                </Box>
              </form>
            </CardContent>
          </Card>
        </Box>

        {/* Add Round Dialog */}
        <Dialog open={openAddRoundDialog} onClose={handleCloseAddRoundDialog}>
          <DialogTitle>Add New Interview Round</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Round Name"
              type="text"
              fullWidth
              variant="outlined"
              value={newRound}
              onChange={handleNewRoundChange}
              error={!!roundError}
              helperText={roundError}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddRoundDialog}>Cancel</Button>
            <Button 
              onClick={handleAddRound} 
              variant="contained" 
              disabled={!newRound.trim() || !!roundError}
            >
              Add
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </LocalizationProvider>
  );
};

export default InterviewScheduleForm;